import React, { useCallback, useMemo, useRef } from "react";
import styled from "styled-components";

import * as flomoji from "@floji-internal/noto-emoji";

import { EmojiSprite } from "--components/emoji";
import { PopperSurface } from "--floponents-web/molecules/PopperSurface";

import EmojiButton, { EmojiWrapper } from "./EmojiButton";

const StyledTonePicker = styled.div``;
const TonePickerPopper = styled(PopperSurface)`
    border-radius: 50px;
    overflow: hidden;
    padding: ${props => props.theme.spacing(0)};

    > div {
        display: flex;
        > div { border-radius: 0; }
    }
`;
const TonePickerToggleButton = styled(EmojiWrapper)``;
const TonePickerResetButton = styled(EmojiWrapper)``;
const TonePickerSeparator = styled.div`
    border-left: 1px solid ${props => props.theme.palette.ui.border};
    margin: 0 ${props => props.theme.spacing(0.5)};
`;

export interface TonePickerProps {
    emoji: flomoji.tPickerEmoji;
    activeTone?: flomoji.tSkinTone;
    isOpen?: boolean;
    onToggled?: (isOpen: boolean) => void;
    onTonePicked?: (tone?: flomoji.tSkinTone) => void;
}
/** Popup UI to select an emoji skin tone. */
const TonePicker = (props: TonePickerProps) => {
    const { onToggled, onTonePicked } = props;

    /** Reference trigger element for popup position. */
    const triggerElement = useRef<HTMLDivElement>(null);

    const handlePopperToggled = useCallback(() => {
        onToggled?.(!props.isOpen);
    }, [onToggled, props.isOpen]);
    const handlePopperClosed = useCallback(() => {
        onToggled?.(false);
    }, [onToggled]);

    const handleTonePicked = useCallback((tone?: flomoji.tSkinTone) => {
        onToggled?.(false);
        onTonePicked?.(tone);
    }, [onToggled, onTonePicked]);
    const handleToneReset = useCallback(() => {
        onToggled?.(false);
        onTonePicked?.();
    }, [onToggled, onTonePicked]);

    // Toggle button emoji sprite with active tone (if set)
    const tonePickerSprite = useMemo(() => (
        props.activeTone
            ? props.emoji.tones?.[props.activeTone]?.sprite
            : props.emoji.sprite
    ), [props.activeTone, props.emoji.sprite, props.emoji.tones]);

    if (!props.emoji.tones || !tonePickerSprite) return null;

    return (
        <StyledTonePicker>
            <TonePickerToggleButton ref={triggerElement} onClick={handlePopperToggled}>
                <EmojiSprite emoji={tonePickerSprite} size={32} spriteSize={64} />
            </TonePickerToggleButton>

            <TonePickerPopper
                triggerRef={triggerElement}
                open={props.isOpen}
                onClose={handlePopperClosed}
                placement="left"
            >
                {Object.keys(props.emoji.tones).map(([tone]) => (
                    <EmojiButton
                        key={tone}
                        emoji={props.emoji}
                        tone={tone as flomoji.tSkinTone}
                        onClick={() => handleTonePicked(tone as flomoji.tSkinTone)}
                    />
                ))}

                <TonePickerSeparator />

                <TonePickerResetButton ref={triggerElement} onClick={handleToneReset}>
                    <EmojiSprite emoji={props.emoji} size={32} spriteSize={64} />
                </TonePickerResetButton>
            </TonePickerPopper>
        </StyledTonePicker>
    );
};

export default TonePicker;
